import { Draggable } from 'react-beautiful-dnd';
import { Typography, Wrapper } from '@screentone/core';

import { Article } from 'components/datamodel/content/Article/Article';
import PageDraftTrash from 'components/page-draft-trash/PageDraftTrash';
import { SearchContent, SearchProps } from 'components/search-content/SearchContent';
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { ArticleItem, OffPlatformListContainer, PublicationSetting, QueryItem } from 'data/generated/graphql';
import { AllessehContent, AllessehQuerySortKeyTypes, AllessehQuerySortOrderType } from 'hooks/useAllessehContentQuery';
import { setJsonQueryStr } from 'utils/collection';
import { determineDateDisplay, formatInTimeZone } from 'utils/dates';

interface ContentSearchProps {
  searchProps: Omit<SearchProps, 'renderContentCard'>;
  embargo?: boolean;
  publicationSettings?: PublicationSetting;
}

const ContentSearch = ({ searchProps, embargo, publicationSettings }: ContentSearchProps) => {
  const { root: data, fromAllessehContent, modifyEntity } = useDataModelContext<OffPlatformListContainer>();
  const { generateDraggableId } = useDragAndDrop();
  const { renderActions } = useContextMenuActions();

  const queryItemIndex = data.collection.findIndex((item) => item.type === EntityType.Query);
  const queryItem = queryItemIndex !== -1 ? (data.collection[queryItemIndex] as QueryItem) : undefined;
  const queryBody = queryItem?.attributes.query;

  let allessehJsonQuery: string | null = null;
  if (queryBody) {
    allessehJsonQuery = JSON.stringify(queryBody);
  }

  const hierarchyId = `0--${data.collection.length}`;

  const handleJsonQueryStr = (newQueryStr: string) => {
    setJsonQueryStr(newQueryStr, queryItemIndex, modifyEntity);
  };

  // When embargo is on, we need to update the searchParams accordingly
  const updatedSearchParams = {
    ...searchProps.searchParams,
    filters: {
      ...searchProps.searchParams.filters,
      author: embargo ? searchProps.defaultFilterState.author : searchProps.searchParams.filters.author,
      publishDates: embargo
        ? searchProps.defaultFilterState.publishDates
        : searchProps.searchParams.filters.publishDates,
      contentTypes: embargo
        ? searchProps.defaultFilterState.contentTypes
        : searchProps.searchParams.filters.contentTypes,
      embargo
    },
    sort: embargo ? AllessehQuerySortKeyTypes.EmbargoDate : searchProps.searchParams.sort,
    sortOrder: embargo ? AllessehQuerySortOrderType.ASC : searchProps.searchParams.sortOrder
  };

  let embargoDate: string;
  let embargoTime: string;
  let formattedDate: string;
  let formattedTime: string;
  let showDate: boolean;
  let showTime: boolean;

  const updatedSearchProps = {
    ...searchProps,
    searchParams: updatedSearchParams,
    defaultFilterState: {
      ...searchProps.defaultFilterState,
      embargo: embargo ?? false
    }
  };

  return (
    <Wrapper padding={{ top: 'sm' }}>
      <SearchContent
        embargo={embargo}
        searchProps={{
          ...updatedSearchProps,
          renderContentCard: (content: AllessehContent, index: number) => (
            <Draggable draggableId={generateDraggableId(DRAGGABLE_PREFIXES.MODULE_ITEM, { content })} index={index}>
              {(draggableProvided) => {
                const articleItem = fromAllessehContent(content) as ArticleItem;

                if (embargo) {
                  const dateObject = new Date(content.data.attributes.embargo_datetime_utc as string);
                  formattedDate = formatInTimeZone(dateObject, publicationSettings?.preferredTimezone, 'LLL d, yyyy');
                  formattedTime = formatInTimeZone(dateObject, publicationSettings?.preferredTimezone, 'h:mm aaaa', {
                    showTimezoneAbbreviation: true
                  });
                  ({ showTime, showDate, embargoTime, embargoDate } = determineDateDisplay(
                    embargoDate,
                    formattedDate,
                    embargoTime,
                    formattedTime
                  ));
                }

                return (
                  <>
                    {embargo && (index < 1 || showDate) && (
                      <Typography margin={{ top: 'sm', bottom: 'md' }} data-testid="page-content-search-date-label">
                        {formattedDate}
                      </Typography>
                    )}
                    {embargo && (index < 1 || showTime) && (
                      <Typography
                        variant="label3"
                        margin={{ bottom: 'sm' }}
                        data-testid="page-content-search-time-label"
                      >
                        {formattedTime}
                      </Typography>
                    )}
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <Article
                        data={articleItem}
                        renderActions={() =>
                          renderActions(MenuActionsKeys.ArticleSearch, {
                            entity: articleItem,
                            hierarchyId,
                            extraProperties: {
                              allessehContent: content,
                              jsonQueryStr: allessehJsonQuery ?? undefined,
                              setJsonQueryStr: handleJsonQueryStr
                            }
                          })
                        }
                      />
                    </div>
                  </>
                );
              }}
            </Draggable>
          )
        }}
        beforeContent={<PageDraftTrash messageLabel="collection" />}
      />
    </Wrapper>
  );
};

export default ContentSearch;
